'use client';

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

// Type des sons possibles
interface SoundContextType {
    isSoundOn: boolean; // État global du son
    toggleSound: () => void; // Activer/Désactiver le son global
    playSound: (type: 'ambiance') => void; // Jouer un son spécifique
    playIntro: (play: boolean) => void; // Lancer ou arrêter l'intro en boucle
    stopSound: () => void; // Arrêter le son actuel
    stopAllSounds: () => void; // Arrêter tous les sons
}

// Contexte Son
const SoundContext = createContext<SoundContextType | undefined>(undefined);

// Fournisseur du contexte Son
export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isSoundOn, setIsSoundOn] = useState(true);
    const introAudioRef = useRef<HTMLAudioElement | null>(null); // Garde une instance d'intro
    const audioRef = useRef<HTMLAudioElement | null>(null); // Garde une instance pour les autres sons
    const currentSound = useRef<string | null>(null);
    const lastSoundRef = useRef<'ambiance' | null>(null);

    // Sons à jouer pour différents événements
    const sounds = {
        ambiance: '/sons/ambiance.mp3',
    };

    useEffect(() => {
        // Dès que la route change, on coupe les sons normaux (sauf ambiance)
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            currentSound.current = null;
        }
        // ⛔ on ne touche pas à `introAudioRef` ici, car il est supposé tourner en fond
    }, []);

    useEffect(() => {
        if (!isSoundOn) return;

        // priorité à l'intro si elle existe
        if (introAudioRef.current) {
            introAudioRef.current.play().catch((e) => console.warn('Playback error:', e));
            return;
        }

        // sinon on relance le dernier son si tu veux
        if (currentSound.current === 'ambiance') {
            playSound('ambiance');
        }
    }, [isSoundOn]);

    // Basculer le son global (activer/désactiver)
    const toggleSound = () => {
        setIsSoundOn((prev) => {
            const newState = !prev;

            if (!newState) {
                // OFF => pause (sans reset)
                audioRef.current?.pause();
                introAudioRef.current?.pause();
            } else {
                // ON => relance ce qu'on avait
                if (introAudioRef.current) {
                    introAudioRef.current.play().catch((e) => console.warn('Playback error:', e));
                } else if (audioRef.current) {
                    audioRef.current.play().catch((e) => console.warn('Playback error:', e));
                } else {
                    // si rien n'existe encore, on relance ambiance par défaut
                    playSound(lastSoundRef.current ?? 'ambiance');
                }
            }

            return newState;
        });
    };

    const stopAllSounds = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }

        if (introAudioRef.current) {
            introAudioRef.current.pause();
            introAudioRef.current.currentTime = 0;
        } else {
            console.warn('❌ introAudioRef est null');
        }

        setIsSoundOn(false);
    };

    // Jouer un son spécifique (hors intro)
    const playSound = (type: 'ambiance') => {
        if (!isSoundOn) return;

        lastSoundRef.current = type;
        // si c'est déjà le même son mais en pause => on relance
        if (currentSound.current === type && audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current.play().catch((e) => console.warn('Playback error:', e));
            }
            return;
        }

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }

        audioRef.current = new Audio(sounds[type]);
        audioRef.current.volume = 0.1;
        audioRef.current.play().catch((e) => console.warn('Playback error:', e));

        currentSound.current = type;
    };

    const stopSound = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    };

    // Jouer ou arrêter "intro" en boucle
    const playIntro = (play: boolean) => {
        if (!isSoundOn || !play) {
            // Même si `play === true`, on refuse si `isSoundOn === false`
            introAudioRef.current?.pause();
            introAudioRef.current && (introAudioRef.current.currentTime = 0);
            return;
        }

        if (play) {
            lastSoundRef.current = 'ambiance';
            if (!introAudioRef.current) {
                introAudioRef.current = new Audio(sounds.ambiance);
                introAudioRef.current.loop = true; // Active la boucle
                introAudioRef.current.volume = 0.1; // Réduit le volume de fond
            }

            introAudioRef.current.play().catch((e) => console.warn('Playback error:', e));
        } else {
            if (introAudioRef.current) {
                introAudioRef.current.pause();
                introAudioRef.current.currentTime = 0;
            }
        }
    };

    return (
        <SoundContext.Provider
            value={{
                isSoundOn,
                toggleSound,
                playSound,
                playIntro,
                stopSound,
                stopAllSounds,
            }}
        >
            {children}
        </SoundContext.Provider>
    );
};

// Hook pour utiliser le Contexte Son
export const useSound = () => {
    const context = useContext(SoundContext);
    if (!context) {
        throw new Error('useSound doit être utilisé dans un SoundProvider');
    }
    return context;
};
